import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, newKey } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useInHouse, useMenu, useMenuCombos, useMyShift, useOrder, useRsMutation, useTables, type Bill, type ChosenModifier, type LineStatus, type MenuItem, type ModifierGroup, type Order, type OrderLine, type Tender } from '../api/restaurant';
import { formatGhs, parseCedis, type Tone } from '../format';
import { RefundDialog } from './Refund';
import { useQueryClient } from '@tanstack/react-query';
import { patchOrder, withLine } from '../offline/optimistic';
import { Badge, Button, Empty, ErrorNote, Field, Icon, Loading, Modal, useReport, useToast } from '../ui/kit';

const LINE: Record<LineStatus, { label: string; tone: Tone }> = {
  NEW: { label: 'Not sent', tone: '' },
  QUEUED: { label: 'Sent', tone: 'info' },
  ACCEPTED: { label: 'Cooking', tone: 'warn' },
  READY: { label: 'Ready', tone: 'good' },
  SERVED: { label: 'Served', tone: 'brand' },
  REJECTED: { label: 'Refused', tone: 'bad' },
  VOID: { label: 'Removed', tone: '' },
};
const dead = (s: LineStatus) => s === 'VOID' || s === 'REJECTED';

export function RestaurantOrder() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const order = useOrder(id);
  const toast = useToast();
  const report = useReport();
  const [dlg, setDlg] = useState<null | 'bill' | 'cancel' | 'move'>(null);
  const [removing, setRemoving] = useState<OrderLine | null>(null);
  const [paying, setPaying] = useState<Bill | null>(null);
  const [refunding, setRefunding] = useState<Bill | null>(null);

  const qc = useQueryClient();
  // A waiter whose signal drops can keep adding and sending items on an order that is already open; they go through
  // when the signal returns. Cancelling, billing and moving a table need a live answer, so they are not saved for later.
  const queueFor = (v: { path: string; method?: 'POST' | 'PATCH'; body?: Record<string, unknown> }) => {
    if (v.path === '/send') return { queue: { label: 'Send the order to the kitchen and bar', optimistic: () => patchOrder(qc, id, (o) => ({ ...o, lines: o.lines.map((l) => (l.status === 'NEW' ? { ...l, status: 'QUEUED' as const } : l)) })) } };
    const m = /^\/lines\/([0-9a-f-]{36})(\/void)?$/.exec(v.path);
    if (m && m[2] && v.body?.reason === undefined) return { queue: { label: 'Remove an item', optimistic: () => patchOrder(qc, id, (o) => withLine(o, m[1]!, (l) => ({ ...l, status: 'VOID' as const }))) } };
    if (m && !m[2] && v.method === 'PATCH' && typeof v.body?.qty === 'number') {
      const qty = v.body.qty;
      return { queue: { label: 'Change a quantity', optimistic: () => patchOrder(qc, id, (o) => withLine(o, m[1]!, (l) => ({ ...l, qty }))) } };
    }
    return {};
  };
  const call = useRsMutation((v: { path: string; method?: 'POST' | 'PATCH'; body?: Record<string, unknown> }) => api<Order>(`/orders/${id}${v.path}`, { method: v.method ?? 'POST', body: v.body ?? {}, ...queueFor(v) }));
  const run = (path: string, done?: string, body?: Record<string, unknown>, method?: 'POST' | 'PATCH', then?: () => void) =>
    call.mutate(
      { path, ...(body ? { body } : {}), ...(method ? { method } : {}) },
      {
        onSuccess: () => {
          if (done) toast.ok(done);
          then?.();
        },
        onError: report,
      },
    );
  const serve = useRsMutation((lineId: string) => api(`/order-lines/${lineId}/serve`, { method: 'POST', body: {} }));

  if (order.isLoading) return <div className="page"><Loading /></div>;
  if (order.error || !order.data) return <div className="page"><ErrorNote error={order.error} retry={() => void order.refetch()} /></div>;

  const o = order.data;
  const live = o.lines.filter((l) => !dead(l.status));
  const unbilled = live.filter((l) => l.status !== 'NEW' && !l.billId);
  const closed = o.status !== 'OPEN';
  const where = o.table ? `Table ${o.table.name}` : (o.label ?? 'Takeaway');
  const openBills = o.bills.filter((b) => b.status === 'OPEN');

  return (
    <div className="page">
      <Link to="/restaurant" className="row small" style={{ textDecoration: 'none' }}>
        <span style={{ width: 18, height: 18 }}>
          <Icon name="back" />
        </span>
        Floor
      </Link>
      <div className="page-head">
        <div>
          <div className="eyebrow num">{o.reference}</div>
          <h1 className="h1">{where}</h1>
          <div className="row small muted">
            <Badge tone={o.status === 'OPEN' ? 'warn' : o.status === 'PAID' ? 'good' : ''}>{o.status === 'OPEN' ? 'Open' : o.status === 'PAID' ? 'Paid' : 'Cancelled'}</Badge>
            <span>{o.covers} guest{o.covers === 1 ? '' : 's'}</span>
            <span>· {o.waiter}</span>
          </div>
        </div>
        <div className="num" style={{ fontSize: '1.5rem', fontFamily: 'var(--display, inherit)' }}>{formatGhs(o.total)}</div>
      </div>

      <div className="grid-2">
        <section className="card" aria-labelledby="ticket">
          <div className="card-head">
            <h2 className="h2" id="ticket">Order</h2>
          </div>
          {live.length === 0 && o.lines.length === 0 ? (
            <Empty icon="inbox" title="Nothing ordered yet">Pick items from the menu.</Empty>
          ) : (
            <ul className="ticket">
              {o.lines.map((l) => (
                <li key={l.id} className={dead(l.status) ? 'dead' : ''}>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div>
                        <strong className="num">{l.qty}×</strong> {l.itemName}
                        {l.variantName && l.variantName !== 'Regular' ? <span className="muted"> · {l.variantName}</span> : null}
                      </div>
                      {l.modifiers.length > 0 ? <div className="hint">{l.modifiers.map((m) => m.name).join(' · ')}</div> : null}
                      {l.comboName ? <div className="hint">Part of {l.comboName}</div> : null}
                      {l.note ? <div className="hint">“{l.note}”</div> : null}
                      {l.reason ? <div className="hint">{l.reason}</div> : null}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="num">{formatGhs(l.total)}</div>
                      <Badge tone={LINE[l.status].tone}>{LINE[l.status].label}</Badge>
                    </div>
                  </div>
                  {l.id.startsWith('local-') ? <span className="hint">Waiting to be sent: it will appear for the kitchen when the connection returns.</span> : null}
                  {!closed && !dead(l.status) && !l.id.startsWith('local-') ? (
                    <div className="row">
                      {l.status === 'NEW' ? (
                        <>
                          <Button disabled={l.qty <= 1 || call.isPending} aria-label="One less" onClick={() => run(`/lines/${l.id}`, undefined, { qty: l.qty - 1 }, 'PATCH')}>−</Button>
                          <Button disabled={call.isPending} aria-label="One more" onClick={() => run(`/lines/${l.id}`, undefined, { qty: l.qty + 1 }, 'PATCH')}>+</Button>
                        </>
                      ) : null}
                      {l.status === 'READY' && can('order.modify') ? (
                        <Button variant="primary" loading={serve.isPending} onClick={() => serve.mutate(l.id, { onError: report, onSuccess: () => toast.ok('Marked as served') })}>Served</Button>
                      ) : null}
                      {!l.billId && (l.status === 'NEW' || can('order.void')) ? (
                        <Button variant="ghost" onClick={() => (l.status === 'NEW' ? run(`/lines/${l.id}/void`) : setRemoving(l))}>Remove</Button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {!closed ? (
            <div className="stack" style={{ marginTop: 12 }}>
              {o.unsentCount > 0 ? (
                <Button variant="primary" block loading={call.isPending} onClick={() => run('/send', 'Sent to the kitchen and bar')}>
                  Send {o.unsentCount} item{o.unsentCount === 1 ? '' : 's'}
                </Button>
              ) : null}
              {can('bill.settle') && unbilled.length > 0 ? (
                <Button block variant={o.unsentCount === 0 ? 'primary' : ''} onClick={() => setDlg('bill')}>
                  Bill {unbilled.length} item{unbilled.length === 1 ? '' : 's'}
                </Button>
              ) : null}
              <div className="row">
                {o.target === 'TABLE' ? <Button onClick={() => setDlg('move')}>Move table</Button> : null}
                <Button variant="danger" onClick={() => setDlg('cancel')}>Cancel order</Button>
              </div>
            </div>
          ) : null}
        </section>

        <div className="stack">
          {o.bills.length > 0 ? (
            <section className="card" aria-labelledby="bills">
              <div className="card-head">
                <h2 className="h2" id="bills">Bills</h2>
              </div>
              <div className="list">
                {o.bills.map((b) => (
                  <div key={b.id} className="item">
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="title num">{b.number}</span>
                      <Badge tone={b.status === 'PAID' ? 'good' : b.status === 'VOID' || b.status === 'REFUNDED' ? '' : 'warn'}>{b.status === 'PAID' ? 'Paid' : b.status === 'VOID' ? 'Void' : b.status === 'REFUNDED' ? 'Refunded' : 'To pay'}</Badge>
                    </div>
                    <div className="row small muted">
                      <span className="num">{formatGhs(b.total)}</span>
                      {b.discount > 0 ? <span>· includes {formatGhs(b.discount)} discount</span> : null}
                      {b.status === 'OPEN' && b.paid > 0 ? <span>· {formatGhs(b.paid)} paid</span> : null}
                    </div>
                    <div className="row">
                      {b.status === 'OPEN' ? <Button variant="primary" onClick={() => setPaying(b)}>Take payment · {formatGhs(b.due)}</Button> : null}
                      {b.status === 'PAID' && b.total > 0 && can('refund.issue') ? <Button variant="ghost" onClick={() => setRefunding(b)}>Refund</Button> : null}
                      {b.status !== 'VOID' ? <Link to={`/restaurant/bills/${b.id}/receipt`} className="btn">{b.status === 'PAID' ? 'Receipt' : 'Print bill'}</Link> : null}
                      {b.status === 'OPEN' && b.payments.length === 0 && can('order.void') ? <VoidBill bill={b} orderId={id} /> : null}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          {!closed ? <MenuPicker orderId={id} lines={o.lines} /> : null}
        </div>
      </div>

      <BillDialog open={dlg === 'bill'} onClose={() => setDlg(null)} orderId={id} lines={unbilled} />
      <ReasonDialog open={dlg === 'cancel'} onClose={() => setDlg(null)} title="Cancel this order?" confirm="Cancel order" pending={call.isPending} onSubmit={(reason) => run('/cancel', 'Order cancelled', { reason }, 'POST', () => setDlg(null))} />
      <ReasonDialog open={!!removing} onClose={() => setRemoving(null)} title={`Remove ${removing?.itemName ?? 'item'}?`} confirm="Remove" pending={call.isPending} onSubmit={(reason) => run(`/lines/${removing!.id}/void`, 'Item removed', { reason }, 'POST', () => setRemoving(null))} />
      <MoveDialog open={dlg === 'move'} onClose={() => setDlg(null)} orderId={id} />
      {refunding ? <RefundDialog title={`Refund ${refunding.number}`} path={`/bills/${refunding.id}/refund`} paid={refunding.payments.map((p) => ({ method: p.tender, amount: p.amount }))} onClose={() => setRefunding(null)} /> : null}
      {paying ? <PayDialog key={paying.id} bill={o.bills.find((b) => b.id === paying.id) ?? paying} order={o} onClose={() => setPaying(null)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ menu */
function MenuPicker({ orderId, lines }: { orderId: string; lines: OrderLine[] }) {
  const menu = useMenu();
  const toast = useToast();
  const report = useReport();
  const [cat, setCat] = useState<string>('');
  const [search, setSearch] = useState('');
  const [choose, setChoose] = useState<MenuItem | null>(null);
  const combos = useMenuCombos().data ?? [];
  const addCombo = useRsMutation((comboId: string) => api(`/orders/${orderId}/combos`, { method: 'POST', body: { clientLineId: newKey(), comboId, qty: 1 } }));
  const qc = useQueryClient();
  const add = useRsMutation((v: { variantId: string; qty: number; note?: string; item: MenuItem; modifierIds?: string[]; chosen?: ChosenModifier[] }) => {
    const clientLineId = newKey();
    const variant = v.item.variants.find((x) => x.id === v.variantId)!;
    const extra = (v.chosen ?? []).reduce((s, m) => s + m.price, 0);
    const { item, chosen, ...rest } = v;
    return api(`/orders/${orderId}/lines`, {
      method: 'POST',
      body: { clientLineId, ...rest },
      queue: {
        label: `Add ${item.name}`,
        optimistic: () =>
          patchOrder(qc, orderId, (o) => ({
            ...o,
            lines: [...o.lines, { id: `local-${clientLineId}`, itemName: item.name, variantName: variant.name, department: item.department, station: item.station, unitPrice: variant.price + extra, qty: v.qty, total: (variant.price + extra) * v.qty, note: v.note ?? null, modifiers: chosen ?? [], comboGroupId: null, comboName: null, status: 'NEW' as const, billId: null, reason: null, sentAt: null, readyAt: null }],
          })),
      },
    });
  });
  const bump = useRsMutation((v: { lineId: string; qty: number }) => api(`/orders/${orderId}/lines/${v.lineId}`, { method: 'PATCH', body: { qty: v.qty }, queue: { label: 'Change a quantity', optimistic: () => patchOrder(qc, orderId, (o) => withLine(o, v.lineId, (l) => ({ ...l, qty: v.qty }))) } }));

  const cats = menu.data ?? [];
  const showCombos = cat === 'combos' && combos.length > 0;
  const active = showCombos ? undefined : (cats.find((c) => c.id === cat) ?? cats[0]);
  const q = search.trim().toLowerCase();
  const items = q ? cats.flatMap((c) => c.items).filter((i) => i.name.toLowerCase().includes(q)) : (active?.items ?? []);

  const tap = (i: MenuItem) => {
    if (i.soldOut || !i.variants.some((v) => v.inStock)) return;
    if (i.variants.length !== 1 || i.modifierGroups.length > 0) return setChoose(i);
    // tapping the same dish again raises the quantity of the unsent line instead of making a second one
    const same = lines.find((l) => !l.id.startsWith('local-') && l.status === 'NEW' && l.itemName === i.name && l.variantName === i.variants[0]!.name && !l.note);
    if (same && same.qty < 50) bump.mutate({ lineId: same.id, qty: same.qty + 1 }, { onError: report, onSuccess: () => toast.ok(`${same.qty + 1}× ${i.name}`) });
    else add.mutate({ variantId: i.variants[0]!.id, qty: 1, item: i }, { onError: report, onSuccess: () => toast.ok(`Added ${i.name}`) });
  };

  return (
    <section className="card" aria-labelledby="menu">
      <div className="card-head">
        <h2 className="h2" id="menu">Menu</h2>
      </div>
      {menu.isLoading ? (
        <Loading rows={2} />
      ) : menu.error ? (
        <ErrorNote error={menu.error} retry={() => void menu.refetch()} />
      ) : cats.length === 0 ? (
        <Empty icon="inbox" title="The menu is empty">A manager can add dishes under Menu and tables.</Empty>
      ) : (
        <div className="stack">
          <input className="input" placeholder="Search the menu" aria-label="Search the menu" value={search} onChange={(e) => setSearch(e.target.value)} />
          {!q ? (
            <div className="seg" role="group" aria-label="Category" style={{ overflowX: 'auto' }}>
              {cats.map((c) => (
                <button key={c.id} type="button" aria-pressed={(active?.id ?? '') === c.id} onClick={() => setCat(c.id)}>{c.name}</button>
              ))}
              {combos.length > 0 ? <button type="button" aria-pressed={showCombos} onClick={() => setCat('combos')}>Combos</button> : null}
            </div>
          ) : null}
          <div className="menu-grid">
            {showCombos && !q
              ? combos.map((c) => (
                  <button key={c.id} type="button" className="menu-item" disabled={!c.available || addCombo.isPending} onClick={() => addCombo.mutate(c.id, { onError: report, onSuccess: () => toast.ok(`Added ${c.name}`) })}>
                    <span className="title">{c.name}</span>
                    <span className="small muted">{c.parts.join(' + ')}</span>
                    <span className="small muted num">{c.available ? formatGhs(c.price) : 'Not available'}</span>
                  </button>
                ))
              : null}
            {(showCombos && !q ? [] : items).map((i) => (
              <button key={i.id} type="button" className="menu-item" disabled={i.soldOut || !i.variants.some((v) => v.inStock) || add.isPending || bump.isPending} onClick={() => tap(i)}>
                <span className="title">{i.name}</span>
                <span className="small muted num">{i.soldOut ? 'Sold out' : !i.variants.some((v) => v.inStock) ? 'Out of stock' : i.variants.length > 1 ? `from ${formatGhs(Math.min(...i.variants.map((v) => v.price)))}` : formatGhs(i.variants[0]?.price ?? 0)}</span>
              </button>
            ))}
            {items.length === 0 && !showCombos ? <p className="small muted">Nothing here.</p> : null}
          </div>
        </div>
      )}
      <VariantDialog item={choose} onClose={() => setChoose(null)} pending={add.isPending} onAdd={(variantId, qty, note, modifierIds, chosen) => add.mutate({ variantId, qty, item: choose!, ...(note ? { note } : {}), ...(modifierIds.length ? { modifierIds, chosen } : {}) }, { onError: report, onSuccess: () => { toast.ok('Added'); setChoose(null); } })} />
    </section>
  );
}

function VariantDialog({ item, onClose, onAdd, pending }: { item: MenuItem | null; onClose: () => void; onAdd: (variantId: string, qty: number, note: string, modifierIds: string[], chosen: ChosenModifier[]) => void; pending: boolean }) {
  const [variant, setVariant] = useState('');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  useEffect(() => {
    setVariant(item?.variants.find((v) => v.inStock)?.id ?? item?.variants[0]?.id ?? '');
    setQty(1);
    setNote('');
    setPicked([]);
  }, [item]);

  const groups = item?.modifierGroups ?? [];
  const chosen: ChosenModifier[] = groups.flatMap((g) => g.modifiers.filter((m) => picked.includes(m.id)).map((m) => ({ group: g.name, name: m.name, price: m.price })));
  const base = item?.variants.find((v) => v.id === variant)?.price ?? 0;
  const unit = base + chosen.reduce((s, m) => s + m.price, 0);
  const usable = (g: ModifierGroup) => g.modifiers.filter((m) => m.active).length;
  const missing = groups.find((g) => picked.filter((id) => g.modifiers.some((m) => m.id === id)).length < Math.min(g.minChoices, usable(g)));
  const toggle = (g: ModifierGroup, id: string) =>
    setPicked((cur) => {
      const inGroup = cur.filter((x) => g.modifiers.some((m) => m.id === x));
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (g.maxChoices === 1) return [...cur.filter((x) => !inGroup.includes(x)), id];
      return inGroup.length >= g.maxChoices ? cur : [...cur, id];
    });

  return (
    <Modal open={!!item} onClose={onClose} title={item?.name ?? 'Item'}>
      {item ? (
        <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (variant && !missing) onAdd(variant, qty, note.trim(), picked, chosen); }}>
          {item.variants.length > 1 ? (
            <div className="stack" role="radiogroup" aria-label="Size">
              {item.variants.map((v) => (
                <label key={v.id} className="choice">
                  <input type="radio" name="variant" checked={variant === v.id} disabled={!v.inStock} onChange={() => setVariant(v.id)} />
                  <span>{v.name}{v.inStock ? '' : ' (out of stock)'}</span>
                  <span className="num">{formatGhs(v.price)}</span>
                </label>
              ))}
            </div>
          ) : null}
          {groups.map((g) => (
            <fieldset key={g.id} className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="small muted" style={{ padding: 0 }}>
                {g.name}
                {g.minChoices > 0 ? ' · required' : g.maxChoices > 1 ? ` · up to ${g.maxChoices}` : ''}
              </legend>
              {g.maxChoices === 1 && g.minChoices === 0 ? (
                <label className="choice">
                  <input type="radio" name={`g-${g.id}`} checked={!g.modifiers.some((m) => picked.includes(m.id))} onChange={() => setPicked((cur) => cur.filter((x) => !g.modifiers.some((m) => m.id === x)))} />
                  <span>None</span>
                  <span />
                </label>
              ) : null}
              {g.modifiers.filter((m) => m.active).map((m) => (
                <label key={m.id} className="choice">
                  <input type={g.maxChoices === 1 ? 'radio' : 'checkbox'} name={`g-${g.id}`} checked={picked.includes(m.id)} disabled={g.maxChoices > 1 && !picked.includes(m.id) && g.modifiers.filter((x) => picked.includes(x.id)).length >= g.maxChoices} onChange={() => toggle(g, m.id)} />
                  <span>{m.name}</span>
                  <span className="num">{m.price > 0 ? `+ ${formatGhs(m.price)}` : ''}</span>
                </label>
              ))}
            </fieldset>
          ))}
          <div className="row">
            <Button type="button" aria-label="One less" disabled={qty <= 1} onClick={() => setQty(qty - 1)}>−</Button>
            <strong className="num" style={{ minWidth: 28, textAlign: 'center' }}>{qty}</strong>
            <Button type="button" aria-label="One more" onClick={() => setQty(Math.min(50, qty + 1))}>+</Button>
            <span className="num" style={{ marginLeft: 'auto' }}>{formatGhs(unit * qty)}</span>
          </div>
          <Field id="vn" label="Note for the kitchen (optional)">
            <input id="vn" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. no pepper" />
          </Field>
          {missing ? <p className="hint">Choose {missing.name.toLowerCase()} to continue.</p> : null}
          <div className="modal-foot">
            <Button type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" loading={pending} disabled={!!missing || !variant}>Add</Button>
          </div>
        </form>
      ) : null}
    </Modal>
  );
}

/* --------------------------------------------------------------- dialogs */
function ReasonDialog({ open, onClose, title, confirm, onSubmit, pending }: { open: boolean; onClose: () => void; title: string; confirm: string; onSubmit: (reason: string) => void; pending: boolean }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (!open) setReason(''); }, [open]);
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (reason.trim().length >= 3) onSubmit(reason.trim()); }}>
        <Field id="rr" label="Reason"><input id="rr" className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Back</Button>
          <Button variant="danger" type="submit" disabled={reason.trim().length < 3} loading={pending}>{confirm}</Button>
        </div>
      </form>
    </Modal>
  );
}

function MoveDialog({ open, onClose, orderId }: { open: boolean; onClose: () => void; orderId: string }) {
  const tables = useTables();
  const toast = useToast();
  const report = useReport();
  const move = useRsMutation((tableId: string) => api(`/orders/${orderId}/transfer`, { method: 'POST', body: { tableId } }));
  const free = (tables.data ?? []).filter((t) => !t.openOrder);
  return (
    <Modal open={open} onClose={onClose} title="Move to another table">
      <div className="stack">
        {free.length === 0 ? <p className="small muted">No free tables right now.</p> : null}
        <div className="rooms">
          {free.map((t) => (
            <button key={t.id} type="button" className="room s-VACANT_CLEAN" style={{ textAlign: 'left', cursor: 'pointer' }} disabled={move.isPending} onClick={() => move.mutate(t.id, { onError: report, onSuccess: () => { toast.ok(`Moved to table ${t.name}`); onClose(); } })}>
              <span className="no num">{t.name}</span>
              <span className="small muted">Seats {t.capacity}</span>
            </button>
          ))}
        </div>
        <Button block onClick={onClose}>Back</Button>
      </div>
    </Modal>
  );
}

function VoidBill({ bill, orderId }: { bill: Bill; orderId: string }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const report = useReport();
  const v = useRsMutation((reason: string) => api(`/bills/${bill.id}/void`, { method: 'POST', body: { reason } }));
  void orderId;
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)}>Void bill</Button>
      <ReasonDialog open={open} onClose={() => setOpen(false)} title={`Void bill ${bill.number}?`} confirm="Void bill" pending={v.isPending} onSubmit={(r) => v.mutate(r, { onError: report, onSuccess: () => { toast.ok('Bill voided; its items are free to bill again'); setOpen(false); } })} />
    </>
  );
}

/* ------------------------------------------------------------- make a bill */
function BillDialog({ open, onClose, orderId, lines }: { open: boolean; onClose: () => void; orderId: string; lines: OrderLine[] }) {
  const { can } = useAuth();
  const toast = useToast();
  const report = useReport();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pct, setPct] = useState('');
  const [reason, setReason] = useState('');
  const make = useRsMutation((body: Record<string, unknown>) => api<Bill>(`/orders/${orderId}/bills`, { method: 'POST', body }));

  useEffect(() => {
    if (open) {
      setPicked(new Set(lines.map((l) => l.id)));
      setPct('');
      setReason('');
    }
    // lines are re-read when the dialog opens only; the list must not reshuffle while ticking
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosen = lines.filter((l) => picked.has(l.id));
  const subtotal = chosen.reduce((s, l) => s + l.total, 0);
  const pctNum = pct.trim() === '' ? 0 : Number(pct);
  const pctOk = pct.trim() === '' || (Number.isFinite(pctNum) && pctNum > 0 && pctNum <= 100);
  const discount = pctOk && pctNum > 0 ? Math.round((subtotal * Math.round(pctNum * 100)) / 10_000) : 0;
  const valid = chosen.length > 0 && pctOk && (pctNum === 0 || reason.trim().length >= 3);

  return (
    <Modal open={open} onClose={onClose} title="Make a bill">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          const all = chosen.length === lines.length;
          make.mutate(
            { ...(all ? {} : { lineIds: chosen.map((l) => l.id) }), ...(pctNum > 0 ? { discount: { kind: 'PERCENT', percent: pctNum }, reason: reason.trim() } : {}) },
            { onError: report, onSuccess: () => { toast.ok('Bill made'); onClose(); } },
          );
        }}
      >
        <p className="small muted">Untick items to split the table into separate bills.</p>
        <ul className="checklist" aria-label="Items to bill">
          {lines.map((l) => (
            <li key={l.id}>
              <label>
                <input type="checkbox" checked={picked.has(l.id)} onChange={(e) => { const n = new Set(picked); if (e.target.checked) n.add(l.id); else n.delete(l.id); setPicked(n); }} />
                <span style={{ flex: 1 }}>{l.qty}× {l.itemName}</span>
                <span className="num">{formatGhs(l.total)}</span>
              </label>
            </li>
          ))}
        </ul>
        {can('order.discount') ? (
          <div className="grid-2">
            <Field id="dp" label="Discount % (optional)" error={pctOk ? undefined : 'Between 0 and 100'}>
              <input id="dp" className="input" inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} />
            </Field>
            {pctNum > 0 ? (
              <Field id="dr" label="Why?">
                <input id="dr" className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
            ) : null}
          </div>
        ) : null}
        <div className="total-row">
          <span>{discount > 0 ? `Total after ${formatGhs(discount)} discount` : 'Total'}</span>
          <span className="amount num">{formatGhs(subtotal - discount)}</span>
        </div>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Make bill</Button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------------------------------------------------- payment */
const TENDERS: { id: Tender; label: string }[] = [
  { id: 'CASH', label: 'Cash' },
  { id: 'MOMO', label: 'MoMo' },
  { id: 'CARD', label: 'Card' },
  { id: 'ROOM', label: 'Room' },
];

function PayDialog({ bill, order, onClose }: { bill: Bill; order: Order; onClose: () => void }) {
  const { can } = useAuth();
  const shift = useMyShift(true);
  const toast = useToast();
  const report = useReport();
  const [tender, setTender] = useState<Tender>('CASH');
  const [amountText, setAmountText] = useState((bill.due / 100).toFixed(2));
  const [received, setReceived] = useState('');
  const [reference, setReference] = useState('');
  const [guestSearch, setGuestSearch] = useState('');
  const [guest, setGuest] = useState<string | null>(bill.bookingId ?? order.bookingId);
  const key = useRef(newKey()); // one key per attempt: a resend after a dropped connection cannot take the money twice
  const guests = useInHouse(guestSearch.trim(), tender === 'ROOM' && !guest);

  const pay = useRsMutation((body: Record<string, unknown>) => api<{ bill: Bill; orderStatus: string }>(`/bills/${bill.id}/pay`, { method: 'POST', body, idempotencyKey: key.current }));
  const amount = tender === 'ROOM' ? bill.due : parseCedis(amountText);
  const amountOk = amount !== null && amount > 0 && amount <= bill.due;
  const refNeeded = tender === 'MOMO' || tender === 'CARD';
  const recv = parseCedis(received);
  const change = tender === 'CASH' && recv !== null && amount !== null && recv > amount ? recv - amount : 0;
  const needShift = tender !== 'ROOM' && !shift.data && !shift.isLoading;
  const valid = amountOk && (!refNeeded || reference.trim().length >= 4) && (tender !== 'ROOM' || !!guest) && !needShift && (tender !== 'ROOM' || can('bill.settle'));

  return (
    <Modal open onClose={onClose} title={`Pay ${bill.number}`}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid || amount === null) return;
          pay.mutate(
            { tender, amount, ...(refNeeded ? { reference: reference.trim() } : {}), ...(tender === 'ROOM' && guest ? { bookingId: guest } : {}) },
            {
              onError: report,
              onSuccess: (r) => {
                key.current = newKey();
                const done = (r as { bill: Bill }).bill.status === 'PAID';
                toast.ok(done ? (change > 0 ? `Paid. Give ${formatGhs(change)} change.` : 'Paid in full') : `${formatGhs(amount)} taken, ${formatGhs((r as { bill: Bill }).bill.due)} still to pay`);
                onClose();
              },
            },
          );
        }}
      >
        <div className="total-row">
          <span>Still to pay</span>
          <span className="amount num">{formatGhs(bill.due)}</span>
        </div>
        <div className="seg" role="group" aria-label="How is it paid?">
          {TENDERS.filter((t) => t.id !== 'ROOM' || bill.paid === 0).map((t) => (
            <button key={t.id} type="button" aria-pressed={tender === t.id} onClick={() => setTender(t.id)}>{t.label}</button>
          ))}
        </div>

        {needShift ? <div className="banner warn">Open your till session first. <Link to="/till" onClick={onClose}>Go to the till</Link></div> : null}

        {tender === 'ROOM' ? (
          guest ? (
            <div className="banner info">Charging the whole bill to the guest’s room. {bill.bookingId || order.bookingId ? '' : <button type="button" className="linklike" onClick={() => setGuest(null)}>Choose someone else</button>}</div>
          ) : (
            <div className="stack">
              <Field id="pg" label="Room number or guest name"><input id="pg" className="input" value={guestSearch} onChange={(e) => setGuestSearch(e.target.value)} autoComplete="off" autoFocus /></Field>
              <div className="list">
                {(guests.data ?? []).map((g) => (
                  <button key={g.bookingId} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setGuest(g.bookingId)}>
                    <span className="title">Room {g.room ?? '?'} · {g.guest}</span>
                  </button>
                ))}
              </div>
            </div>
          )
        ) : (
          <>
            <Field id="pa" label="Amount to take now" hint="Part-payments are fine; the rest can be paid another way" error={amount !== null && amount > bill.due ? 'More than is owed' : undefined}>
              <input id="pa" className="input" inputMode="decimal" value={amountText} onChange={(e) => setAmountText(e.target.value)} />
            </Field>
            {tender === 'CASH' ? (
              <Field id="pr" label="Cash received (optional)" hint={change > 0 ? `Change to give: ${formatGhs(change)}` : 'To work out the change'}>
                <input id="pr" className="input" inputMode="decimal" value={received} onChange={(e) => setReceived(e.target.value)} />
              </Field>
            ) : (
              <Field id="pf" label={tender === 'MOMO' ? 'MoMo transaction ID' : 'Card slip number'} hint={tender === 'MOMO' ? 'From the payment confirmation message. Check the money has arrived first.' : 'From the card terminal slip. Never type a card number.'}>
                <input id="pf" className="input" value={reference} onChange={(e) => setReference(e.target.value)} autoCapitalize="characters" />
              </Field>
            )}
          </>
        )}

        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={pay.isPending}>
            {tender === 'ROOM' ? 'Charge to room' : `Take ${amount !== null && amount > 0 ? formatGhs(amount) : 'payment'}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

